import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Check, X, ChevronRight, Camera, Gauge, AlertTriangle,
  Wrench, Shield, TrendingUp, Users, Clock, Phone, Play,
  Zap, Target, BarChart3, Eye, FileCheck, MessageSquare,
  CheckCircle2, XCircle, MinusCircle
} from "lucide-react";
import { Slider } from "../../components/ui/slider";
import ProductWalkthrough from "./ProductWalkthrough";
import CinematicDemo from "../../components/demo/CinematicDemo";

// ─── Design tokens (Jewel & Luxury Dark) ───
const fonts = {
  heading: "'Cabinet Grotesk', sans-serif",
  body: "'Manrope', sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

const colors = {
  bg: "#050505",
  surface: "#0F0F0F",
  surfaceGlass: "rgba(15, 15, 15, 0.7)",
  primary: "#10B981",
  primaryHover: "#059669",
  red: "#EF4444",
  redSoft: "rgba(239, 68, 68, 0.1)",
  redText: "#FCA5A5",
  redBorder: "rgba(239, 68, 68, 0.3)",
  amber: "#F59E0B",
  amberSoft: "rgba(245, 158, 11, 0.1)",
  amberText: "#FCD34D",
  amberBorder: "rgba(245, 158, 11, 0.3)",
  green: "#10B981",
  greenSoft: "rgba(16, 185, 129, 0.1)",
  greenText: "#6EE7B7",
  greenBorder: "rgba(16, 185, 129, 0.3)",
  text: "#FAFAFA",
  muted: "#A3A3A3",
  subtle: "#525252",
  border: "#262626",
};

const media = {
  brakeSuspension: "https://static.prod-images.emergentagent.com/jobs/d4d82416-17be-4369-a170-424c572c50af/images/d4829056fbcc8a427c9e6be9d287367df5413bef3376e6e85aadc9e65af2c914.png",
  diagnosticScanner: "https://static.prod-images.emergentagent.com/jobs/d4d82416-17be-4369-a170-424c572c50af/images/d0be010fb5ccd4ba61dfe74638d9178ad2a4c229018cb4291820a991cd6c84c6.png",
  garageOwner: "https://static.prod-images.emergentagent.com/jobs/d4d82416-17be-4369-a170-424c572c50af/images/604abc78199847a8c5a1faf3b0b0facbace6568bf7320255629d9f158662c8e0.png",
};

// ─── Utility components ───
const AnimatedNumber = ({ value, prefix = "£", duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = Math.ceil(value / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= value) { start = value; clearInterval(timer); }
      setDisplay(start);
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span>{prefix}{display.toLocaleString()}</span>;
};

const ProgressBar = ({ current, total }) => (
  <div className="flex gap-2 w-full max-w-xs">
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} className="flex-1 h-1 rounded-full transition-all duration-500"
        style={{ backgroundColor: i <= current ? colors.primary : colors.border }} />
    ))}
  </div>
);

const SectionLabel = ({ children }) => (
  <span style={{ fontFamily: fonts.body, letterSpacing: "0.2em" }}
    className="text-[11px] font-semibold uppercase text-neutral-500 block mb-3"
  >{children}</span>
);

// ═══════════════════════════════════════
// RED / AMBER / GREEN DECISION COMPONENTS
// ═══════════════════════════════════════
const STATUS_CONFIG = {
  red: {
    label: "RED", bg: colors.redSoft, border: colors.redBorder,
    text: colors.redText, dot: colors.red, icon: XCircle, meaning: "Safety Critical",
  },
  amber: {
    label: "AMBER", bg: colors.amberSoft, border: colors.amberBorder,
    text: colors.amberText, dot: colors.amber, icon: MinusCircle, meaning: "Requires Attention",
  },
  green: {
    label: "GREEN", bg: colors.greenSoft, border: colors.greenBorder,
    text: colors.greenText, dot: colors.green, icon: CheckCircle2, meaning: "No Action Required",
  },
};

const StatusBadge = ({ status, showLabel = true, size = "sm" }) => {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return null;
  const sz = size === "sm" ? "text-[10px] px-2 py-0.5" : "text-xs px-3 py-1";
  return (
    <span data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1.5 font-bold uppercase tracking-wider rounded-sm ${sz}`}
      style={{ backgroundColor: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}>
      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: cfg.dot }} />
      {showLabel && cfg.label}
    </span>
  );
};

const StatusBorderCard = ({ status, children, className = "" }) => {
  const borderColor = status === "red" ? colors.red : status === "amber" ? colors.amber : colors.green;
  const bgColor = status === "red" ? colors.redSoft : status === "amber" ? colors.amberSoft : colors.greenSoft;
  return (
    <div className={`border-l-[3px] rounded-sm ${className}`}
      style={{ borderLeftColor: borderColor, backgroundColor: bgColor }}>
      {children}
    </div>
  );
};

// ═══════════════════════════════════════
// BRAKE SIMULATION (RED)
// ═══════════════════════════════════════
const BrakeSimulation = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const steps = [
    { label: "Measurement" }, { label: "Evidence" },
    { label: "Classification" }, { label: "Job Build" }, { label: "Customer View" },
  ];
  useEffect(() => {
    if (step < steps.length - 1) {
      const t = setTimeout(() => setStep(s => s + 1), 2400);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => onComplete?.(), 1800);
      return () => clearTimeout(t);
    }
  }, [step]);

  return (
    <div className="bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden" data-testid="brake-simulation">
      <div className="border-b border-white/5 px-5 py-3 flex items-center justify-between bg-neutral-900/50">
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500">Live Simulation</span>
        <span className="text-[11px] font-medium text-neutral-500" style={{ fontFamily: fonts.mono }}>Brake Inspection</span>
      </div>
      <div className="p-6">
        <div className="flex gap-3 mb-6">
          {steps.map((s, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1 rounded-full mb-2 transition-all duration-700 ${i <= step ? 'bg-red-500/100' : 'bg-neutral-800'}`} />
              <span className={`text-[9px] font-semibold uppercase tracking-wider ${i <= step ? 'text-neutral-300' : 'text-neutral-400'}`}>{s.label}</span>
            </div>
          ))}
        </div>
        <div className="min-h-[260px] flex items-center justify-center relative">
          {step === 0 && (
            <div className="animate-fadeIn text-center w-full">
              <div className="inline-flex items-center gap-3 px-6 py-4 bg-white/[0.03] border border-white/10 rounded-lg mb-4">
                <Gauge className="w-5 h-5 text-neutral-500" />
                <span style={{ fontFamily: fonts.mono }} className="text-2xl font-bold tracking-tight text-white">2.5<span className="text-sm text-neutral-500 ml-1">mm</span></span>
              </div>
              <p className="text-sm text-neutral-400 mb-3">Front left brake pad measured at 2.5mm</p>
              <div className="max-w-xs mx-auto">
                <div className="flex h-2 rounded-full overflow-hidden">
                  <div className="flex-[3] bg-red-500/100" />
                  <div className="flex-[2] bg-amber-400" />
                  <div className="flex-[5] bg-emerald-500/100" />
                </div>
                <div className="flex justify-between mt-1.5">
                  <span className="text-[9px] font-bold text-red-400">0mm</span>
                  <span className="text-[9px] font-bold text-red-400">3mm</span>
                  <span className="text-[9px] font-bold text-amber-400">5mm</span>
                  <span className="text-[9px] font-bold text-emerald-400">10mm+</span>
                </div>
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="animate-fadeIn text-center w-full">
              <div className="w-24 h-24 mx-auto bg-white/[0.03] border-2 border-dashed border-neutral-700 rounded-lg flex items-center justify-center mb-4">
                <Camera className="w-8 h-8 text-blue-400 animate-pulse" />
              </div>
              <div className="flex items-center justify-center gap-2 text-emerald-400">
                <Check className="w-4 h-4" />
                <span className="text-sm font-semibold">Photo captured — evidence logged</span>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="animate-fadeIn text-center w-full">
              <div className="animate-statusReveal"><StatusBadge status="red" size="md" /></div>
              <p className="text-base font-bold text-red-400 mt-3 mb-1">Safety Critical — Immediate Action</p>
              <p className="text-sm text-neutral-400">Below minimum safe threshold. Stopping distance compromised.</p>
              <div className="mt-5 flex gap-3 justify-center">
                <StatusBorderCard status="red" className="px-3 py-2 text-left">
                  <span className="text-[10px] font-bold text-red-400">BELOW 3MM</span>
                  <p className="text-[10px] text-neutral-500">Requires replacement</p>
                </StatusBorderCard>
                <StatusBorderCard status="amber" className="px-3 py-2 text-left opacity-50">
                  <span className="text-[10px] font-bold text-amber-400">3-5MM</span>
                  <p className="text-[10px] text-neutral-400">Monitor</p>
                </StatusBorderCard>
                <StatusBorderCard status="green" className="px-3 py-2 text-left opacity-30">
                  <span className="text-[10px] font-bold text-emerald-400">5MM+</span>
                  <p className="text-[10px] text-neutral-400">No action</p>
                </StatusBorderCard>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="animate-fadeIn w-full">
              <div className="space-y-2">
                <StatusBorderCard status="red" className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusBadge status="red" showLabel={false} />
                    <span className="text-sm font-bold text-white/80">Front Brake Pads (pair)</span>
                  </div>
                  <span className="text-sm font-bold text-white/80">£189.99</span>
                </StatusBorderCard>
                <StatusBorderCard status="red" className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusBadge status="red" showLabel={false} />
                    <span className="text-sm font-bold text-white/80">Front Brake Discs (pair)</span>
                  </div>
                  <span className="text-sm font-bold text-white/80">£245.00</span>
                </StatusBorderCard>
              </div>
              <div className="mt-3 border border-white/10 rounded-lg px-4 py-2 flex items-center justify-between bg-white/[0.03]">
                <span className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider">Total inc VAT</span>
                <span className="text-lg font-bold text-white">£434.99</span>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="animate-fadeIn w-full max-w-sm mx-auto">
              <div className="border border-white/10 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-white px-4 py-2 border-b border-white/10 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">Customer Portal</span>
                  <StatusBadge status="red" />
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-xs text-neutral-300">Your front brake pads measured at 2.5mm — below the safe minimum of 3.0mm.</p>
                  <div className="border-l-2 border-red-500 pl-3">
                    <p className="text-xs text-neutral-300">Braking performance is significantly reduced.</p>
                  </div>
                  <button data-testid="sim-approve-brake" className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-md flex items-center justify-center gap-2 transition-colors">
                    <Check className="w-3 h-3" /> Approve Repair — £434.99
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// STEERING SIMULATION (AMBER)
// ═══════════════════════════════════════
const SteeringSimulation = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const steps = [
    { label: "Finding" }, { label: "Pair Logic" },
    { label: "Alignment" }, { label: "Complete Job" }, { label: "Customer View" },
  ];
  useEffect(() => {
    if (step < steps.length - 1) {
      const t = setTimeout(() => setStep(s => s + 1), 2400);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => onComplete?.(), 1800);
      return () => clearTimeout(t);
    }
  }, [step]);

  return (
    <div className="bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden" data-testid="steering-simulation">
      <div className="border-b border-white/5 px-5 py-3 flex items-center justify-between bg-neutral-900/50">
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-400">Live Simulation</span>
        <span className="text-[11px] font-medium text-neutral-400" style={{ fontFamily: fonts.mono }}>Steering & Suspension</span>
      </div>
      <div className="p-6">
        <div className="flex gap-3 mb-6">
          {steps.map((s, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1 rounded-full mb-2 transition-all duration-700 ${i <= step ? 'bg-amber-500/100' : 'bg-neutral-200'}`} />
              <span className={`text-[9px] font-semibold uppercase tracking-wider ${i <= step ? 'text-neutral-300' : 'text-neutral-300'}`}>{s.label}</span>
            </div>
          ))}
        </div>
        <div className="min-h-[260px] flex items-center justify-center">
          {step === 0 && (
            <div className="animate-fadeIn text-center w-full">
              <div className="animate-statusReveal mb-3"><StatusBadge status="amber" size="md" /></div>
              <p className="text-base font-bold text-amber-400 mb-1">Nearside Track Rod End</p>
              <p className="text-sm text-neutral-500">Excessive play detected during inspection</p>
              <p className="text-xs text-neutral-400 mt-2">Deteriorating — requires attention before MOT</p>
            </div>
          )}
          {step === 1 && (
            <div className="animate-fadeIn text-center w-full">
              <div className="px-5 py-4 bg-blue-500/10 border border-blue-500/20 rounded-lg inline-block">
                <Zap className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                <p className="text-sm font-bold text-blue-700">System Intelligence</p>
                <p className="text-xs text-blue-400 mt-1">"Offside track rod end should be checked — they typically wear together."</p>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="animate-fadeIn text-center w-full">
              <div className="px-5 py-4 bg-blue-500/10 border border-blue-500/20 rounded-lg inline-block">
                <Target className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                <p className="text-sm font-bold text-blue-700">Alignment Suggestion</p>
                <p className="text-xs text-blue-400 mt-1">"Wheel alignment is required after track rod replacement."</p>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="animate-fadeIn w-full">
              <div className="space-y-2">
                <StatusBorderCard status="amber" className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusBadge status="amber" showLabel={false} />
                    <span className="text-sm font-bold text-white/80">Track Rod Ends (both sides)</span>
                  </div>
                  <span className="text-sm font-bold text-white">£249.00</span>
                </StatusBorderCard>
                <StatusBorderCard status="amber" className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusBadge status="amber" showLabel={false} />
                    <span className="text-sm font-bold text-white/80">4-Wheel Alignment</span>
                  </div>
                  <span className="text-sm font-bold text-white">£79.00</span>
                </StatusBorderCard>
              </div>
              <div className="mt-3 border border-white/10 rounded-lg px-4 py-2 flex items-center justify-between bg-white/[0.03]">
                <span className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider">Total inc VAT</span>
                <span className="text-lg font-bold text-white">£328.00</span>
              </div>
              <div className="mt-3 flex items-center gap-2 justify-center">
                <span className="text-[10px] px-2 py-1 bg-emerald-500/10 text-emerald-400 font-bold rounded-sm border border-emerald-500/20">SHARED LABOUR SAVINGS</span>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="animate-fadeIn w-full max-w-sm mx-auto">
              <div className="border border-white/10 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-white px-4 py-2 border-b border-white/10 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">Customer Portal</span>
                  <StatusBadge status="amber" />
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-xs text-neutral-300">Both track rod ends show excessive play, affecting steering precision.</p>
                  <div className="border-l-2 border-amber-500 pl-3">
                    <p className="text-xs text-neutral-300">Addressing now prevents tyre wear and higher costs.</p>
                  </div>
                  <button data-testid="sim-approve-steering" className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-md flex items-center justify-center gap-2 transition-colors">
                    <Check className="w-3 h-3" /> Approve — £328.00
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// DIAGNOSTIC SIMULATION
// ═══════════════════════════════════════
const DiagnosticSimulation = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const steps = [
    { label: "Guided Process" }, { label: "Uncertainty" },
    { label: "Recommendation" }, { label: "AI Explanation" }, { label: "Customer Decision" },
  ];
  useEffect(() => {
    if (step < steps.length - 1) {
      const t = setTimeout(() => setStep(s => s + 1), 2600);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => onComplete?.(), 1800);
      return () => clearTimeout(t);
    }
  }, [step]);

  return (
    <div className="bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden" data-testid="diagnostic-simulation">
      <div className="border-b border-white/5 px-5 py-3 flex items-center justify-between bg-neutral-900/50">
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-400">Live Simulation</span>
        <span className="text-[11px] font-medium text-neutral-400" style={{ fontFamily: fonts.mono }}>Diagnostic Assessment</span>
      </div>
      <div className="p-6">
        <div className="flex gap-3 mb-6">
          {steps.map((s, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1 rounded-full mb-2 transition-all duration-700 ${i <= step ? 'bg-blue-600' : 'bg-neutral-200'}`} />
              <span className={`text-[9px] font-semibold uppercase tracking-wider ${i <= step ? 'text-neutral-300' : 'text-neutral-300'}`}>{s.label}</span>
            </div>
          ))}
        </div>
        <div className="min-h-[260px] flex items-center justify-center">
          {step === 0 && (
            <div className="animate-fadeIn w-full">
              <div className="border border-white/10 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-center">
                    <FileCheck className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white/80">Guided Diagnostic Workflow</p>
                    <p className="text-xs text-neutral-500">Engine management light — customer reported</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {[
                    { text: "Fault codes read", status: "green" },
                    { text: "Live data reviewed", status: "amber" },
                    { text: "Component tests performed", status: "red" },
                  ].map((s, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white/[0.03] rounded-md">
                      <StatusBadge status={s.status} showLabel={false} size="sm" />
                      <span className="text-xs font-medium text-neutral-300">{s.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="animate-fadeIn text-center w-full">
              <div className="px-6 py-4 bg-amber-500/10 border border-amber-500/20 rounded-lg inline-block">
                <AlertTriangle className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <p className="text-sm font-bold text-amber-400">System Identifies Uncertainty</p>
                <p className="text-xs text-amber-400 mt-1">"Multiple possible causes detected. Further investigation required."</p>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="animate-fadeIn w-full">
              <div className="border border-blue-500/20 bg-blue-500/10 rounded-lg p-5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-400 mb-3">Controlled Recommendation</p>
                <div className="bg-white border border-blue-500/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-white/80">Diagnostic Assessment</span>
                    <span className="text-sm font-bold text-white">£89.00 + VAT</span>
                  </div>
                  <p className="text-xs text-neutral-400">Structured investigation. Not guesswork — evidence-based.</p>
                </div>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="animate-fadeIn w-full">
              <div className="border border-white/10 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-4 h-4 text-blue-400" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400">AI Explanation Engine</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">What we found</p>
                    <p className="text-xs text-neutral-300 mt-1">Engine management light on. Fault codes P0171 and P0174 — engine running lean.</p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-md p-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-blue-400">Recommendation</p>
                    <p className="text-xs font-semibold text-white/80 mt-1">Diagnostic assessment to isolate exact cause and provide accurate repair quote.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="animate-fadeIn w-full max-w-sm mx-auto">
              <div className="border border-white/10 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-white px-4 py-2 border-b border-white/10">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">Customer Portal</span>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-xs text-neutral-300">Clear explanation builds trust. Customer understands why diagnosis is needed.</p>
                  <button data-testid="sim-approve-diagnostic" className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-md flex items-center justify-center gap-2 transition-colors">
                    <Check className="w-3 h-3" /> Approve Diagnostic — £89.00
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// DIAGNOSIS QUESTIONS
// ═══════════════════════════════════════
const QUESTIONS = [
  {
    id: "inconsistency",
    question: "Are your technicians missing items or reporting inconsistently?",
    yesText: "Most garages lose £50-£100 per job from missed findings. Yestimate enforces structured inspections with mandatory evidence.",
    simulation: "brake", revenue: 75, img: media.brakeSuspension,
  },
  {
    id: "underpricing",
    question: "Is your front of house missing work or underpricing jobs?",
    yesText: "Yestimate's intelligent job builder automatically includes paired items, alignment requirements, and shared labour — building complete, correctly-priced jobs.",
    simulation: "steering", revenue: 95, img: media.brakeSuspension,
  },
  {
    id: "declining",
    question: "Are customers declining work you know they need?",
    yesText: "Yestimate's Customer Decision Engine presents work clearly, with evidence and structured explanations. Customers understand what's needed — so they approve more.",
    simulation: "brake", revenue: 120, img: media.garageOwner,
  },
  {
    id: "diagnostic",
    question: "Are you losing diagnostic and investigation work?",
    yesText: "Yestimate includes a guided diagnostic workflow with AI-powered customer explanations. Customers see structured reasoning — not guesswork.",
    simulation: "diagnostic", revenue: 89, img: media.diagnosticScanner,
  },
];

// ═══════════════════════════════════════
// MAIN MARKETING PAGE
// ═══════════════════════════════════════
const MarketingHomepage = () => {
  const navigate = useNavigate();
  const [diagnosisStarted, setDiagnosisStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showSimulation, setShowSimulation] = useState(null);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [diagnosisComplete, setDiagnosisComplete] = useState(false);
  const [carsPerWeek, setCarsPerWeek] = useState(20);
  const [showFullDemo, setShowFullDemo] = useState(false);
  const diagnosisRef = useRef(null);
  const resultsRef = useRef(null);
  const calcRef = useRef(null);

  const handleAnswer = (answer) => {
    const q = QUESTIONS[currentQuestion];
    const newAnswers = { ...answers, [q.id]: answer };
    setAnswers(newAnswers);
    if (answer === "yes") {
      setShowSimulation(q.simulation);
      setTotalRevenue(prev => prev + q.revenue);
    } else {
      goToNext();
    }
  };

  const goToNext = () => {
    setShowSimulation(null);
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setDiagnosisComplete(true);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  };

  const startDiagnosis = () => {
    setDiagnosisStarted(true);
    setTimeout(() => diagnosisRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  // Financial calculator
  const lostPerJob = 95;
  const weeklyLoss = carsPerWeek * lostPerJob;
  const yearlyLoss = weeklyLoss * 50;

  const weeklyDiag = totalRevenue * 20;
  const yearlyDiag = weeklyDiag * 50;

  return (
    <div style={{ fontFamily: fonts.body }} className="min-h-screen bg-[#050505]">
      {/* ─── GLOBAL STYLES ─── */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slideUp { animation: slideUp 0.6s ease-out forwards; }
        @keyframes statusReveal { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }
        .animate-statusReveal { animation: statusReveal 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 35s linear infinite; }
        @keyframes pulse-glow { 0%,100% { box-shadow: 0 0 20px rgba(16,185,129,0.1); } 50% { box-shadow: 0 0 40px rgba(16,185,129,0.25); } }
        .animate-pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        @keyframes counter-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-counter { animation: counter-up 0.8s ease-out forwards; }
      `}</style>

      {/* ─── NAV (Glass Dark) ─── */}
      <nav className="fixed top-0 left-0 right-0 bg-[#050505]/80 backdrop-blur-xl z-50 border-b border-white/5" data-testid="nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3 flex items-center justify-between">
          <div className="flex flex-col items-center">
            <img src="/yestimate-logo-light.png" alt="Yestimate" className="h-12 w-auto md:h-16" data-testid="nav-logo"
              style={{ filter: "drop-shadow(0 0 12px rgba(16,185,129,0.15))" }} />
            <span className="text-emerald-400/70 text-[10px] md:text-xs tracking-[0.12em] -mt-0.5 text-center"
              style={{ fontFamily: fonts.body }}>
              Inspect. Report. Authorise.
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/app/login" className="hidden md:inline-flex text-sm font-medium text-neutral-400 hover:text-white transition-colors" data-testid="nav-login">
              Login
            </a>
            <button onClick={startDiagnosis} data-testid="nav-cta"
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500/100 text-white text-sm font-semibold rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-emerald-500/20">
              Start Diagnosis
            </button>
          </div>
        </div>
      </nav>

      {/* ═══════════════════════════════════════ */}
      {/* SECTION 1 — HERO                       */}
      {/* ═══════════════════════════════════════ */}
      <section className="pt-36 pb-16 md:pt-44 md:pb-24 px-6 lg:px-12 relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">

            {/* ─── Left — Copy ─── */}
            <div className="lg:col-span-6">

              {/* 1. HEADLINE */}
              <h1 style={{ fontFamily: fonts.heading }}
                className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold tracking-tighter leading-[1.08] text-white mb-5"
                data-testid="hero-headline">
                There's Work in Every Inspection You're Not Getting Paid For
              </h1>

              {/* 2. SUBHEADLINE */}
              <p className="text-base sm:text-lg text-neutral-300 leading-relaxed max-w-lg mb-3"
                data-testid="hero-subhead">
                Yestimate finds it, builds it properly, and helps you get it authorised.
              </p>

              {/* 3. SUPPORT LINE */}
              <p className="text-sm text-neutral-400 font-medium tracking-wide mb-10"
                data-testid="hero-support-line">
                Find More. Price It Properly. Get It Approved.
              </p>

              {/* 6. METRICS — Revenue dominant, then secondary */}
              <div className="mb-10" data-testid="hero-metrics">
                {/* Primary — Revenue */}
                <div className="bg-emerald-500/[0.06] border border-emerald-500/20 rounded-lg px-5 py-4 mb-3" data-testid="hero-metric-revenue">
                  <p style={{ fontFamily: fonts.mono }} className="text-2xl sm:text-3xl font-black text-emerald-400 tracking-tight">{"\u00A3"}2.4k+
                    <span className="text-sm sm:text-base font-semibold text-emerald-400/70 ml-2">revenue recovered this month</span>
                  </p>
                  <p className="text-[11px] text-neutral-500 mt-1">From live garage usage</p>
                </div>
                {/* Secondary + Third */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/[0.025] border border-white/[0.08] rounded-lg px-4 py-3">
                    <p style={{ fontFamily: fonts.mono }} className="text-lg font-bold text-white">94%</p>
                    <p className="text-[10px] text-neutral-400 uppercase tracking-wider mt-0.5">Customer approval rate</p>
                  </div>
                  <div className="bg-white/[0.025] border border-white/[0.08] rounded-lg px-4 py-3">
                    <p style={{ fontFamily: fonts.mono }} className="text-lg font-bold text-white">0</p>
                    <p className="text-[10px] text-neutral-400 uppercase tracking-wider mt-0.5">Missed items per inspection</p>
                  </div>
                </div>
              </div>

              {/* 4. CTA BUTTONS */}
              <div className="flex flex-col sm:flex-row gap-3 mb-5" data-testid="hero-ctas">
                <button onClick={startDiagnosis} data-testid="hero-cta-primary"
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white text-base font-bold rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-500/25">
                  See Where You're Losing Work
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={() => {
                  // Unlock browser audio context within user gesture
                  const ctx = new (window.AudioContext || window.webkitAudioContext)();
                  ctx.resume().then(() => ctx.close()).catch(() => {});
                  setShowFullDemo(true);
                }} data-testid="hero-see-demo"
                  className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 border-2 border-emerald-500/35 text-emerald-400 hover:text-emerald-300 hover:border-emerald-500/55 hover:bg-emerald-500/[0.06] text-sm font-semibold rounded-lg transition-all">
                  <Play className="w-4 h-4" />
                  Watch How It Works
                </button>
              </div>

              {/* 5. TRUST MICRO-LINE */}
              <p className="text-xs text-neutral-500" data-testid="hero-trust">
                Built inside a real working garage
              </p>
            </div>

            {/* ─── Right — Product UI Visual (Revenue Story) ─── */}
            <div className="lg:col-span-6 relative" data-testid="hero-product-ui">
              {/* Subtle approved glow */}
              <div className="absolute -inset-6 rounded-3xl opacity-40" style={{ background: "radial-gradient(ellipse at center 70%, rgba(16,185,129,0.08) 0%, transparent 60%)" }} />

              <div className="relative bg-[#0C0C0C] rounded-xl border border-white/[0.08] overflow-hidden shadow-[0_12px_48px_rgba(0,0,0,0.4)]">
                {/* Browser chrome */}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-neutral-900/80 border-b border-white/5">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]/70" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]/70" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]/70" />
                  </div>
                  <div className="flex-1 bg-neutral-800/50 rounded-md px-3 py-1 ml-2">
                    <span className="text-[10px] text-neutral-500 font-medium" style={{ fontFamily: fonts.mono }}>app.yestimate.co.uk</span>
                  </div>
                </div>

                {/* App content */}
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-[0.15em]">Job Build Summary</span>
                    <span className="text-[10px] text-neutral-500" style={{ fontFamily: fonts.mono }}>BMW 320d</span>
                  </div>

                  {/* ── "Without" baseline (dimmed, crossed out) ── */}
                  <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg px-4 py-2.5 mb-3 opacity-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[9px] font-bold text-neutral-500 uppercase tracking-wider">Without system</span>
                        <p className="text-[11px] text-neutral-500 mt-0.5 line-through">Brake pads only — no pairing, no filter</p>
                      </div>
                      <span className="text-sm font-bold text-neutral-500 line-through" style={{ fontFamily: fonts.mono }}>{"\u00A3"}89</span>
                    </div>
                  </div>

                  {/* ── "With Yestimate" full job (bright, active) ── */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wider">With Yestimate</span>
                      <div className="flex-1 h-px bg-emerald-500/15" />
                    </div>

                    <div className="space-y-1.5">
                      {/* RED — Brake Pads */}
                      <div className="border-l-[3px] border-red-500 bg-red-500/[0.04] rounded-r-lg px-3 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-[8px] font-bold uppercase px-1 py-0.5 bg-red-500/15 text-red-400 border border-red-500/25 rounded-sm">
                            <span className="w-1 h-1 rounded-full bg-red-500" />RED
                          </span>
                          <span className="text-[12px] font-semibold text-white/80">Front Brake Pads</span>
                          <span className="text-[9px] text-neutral-500">2.5mm</span>
                        </div>
                        <span className="text-[12px] font-bold text-white/60" style={{ fontFamily: fonts.mono }}>{"\u00A3"}189.99</span>
                      </div>

                      {/* RED — Brake Discs (SYSTEM PAIRED) */}
                      <div className="border-l-[3px] border-red-500 bg-red-500/[0.04] rounded-r-lg px-3 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-[8px] font-bold uppercase px-1 py-0.5 bg-red-500/15 text-red-400 border border-red-500/25 rounded-sm">
                            <span className="w-1 h-1 rounded-full bg-red-500" />RED
                          </span>
                          <span className="text-[12px] font-semibold text-white/80">Front Brake Discs</span>
                          <span className="text-[8px] font-bold text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded px-1 py-0.5">PAIRED</span>
                        </div>
                        <span className="text-[12px] font-bold text-white/60" style={{ fontFamily: fonts.mono }}>{"\u00A3"}245.00</span>
                      </div>

                      {/* AMBER — Air Filter (SYSTEM CAUGHT) */}
                      <div className="border-l-[3px] border-amber-500 bg-amber-500/[0.03] rounded-r-lg px-3 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-[8px] font-bold uppercase px-1 py-0.5 bg-amber-500/15 text-amber-400 border border-amber-500/25 rounded-sm">
                            <span className="w-1 h-1 rounded-full bg-amber-500" />AMBER
                          </span>
                          <span className="text-[12px] font-semibold text-white/80">Air Filter</span>
                          <span className="text-[8px] font-bold text-purple-400 bg-purple-500/10 border border-purple-500/20 rounded px-1 py-0.5">INTERVAL</span>
                        </div>
                        <span className="text-[12px] font-bold text-white/60" style={{ fontFamily: fonts.mono }}>{"\u00A3"}34.99</span>
                      </div>
                    </div>
                  </div>

                  {/* ── Revenue comparison bar ── */}
                  <div className="bg-white/[0.025] border border-white/[0.06] rounded-lg px-4 py-3 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider">Revenue per job</span>
                      <span className="text-xs font-bold text-emerald-400" style={{ fontFamily: fonts.mono }}>+{"\u00A3"}345.99</span>
                    </div>
                    {/* Visual bar */}
                    <div className="flex-1 h-2.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className="flex h-full">
                        <div className="h-full bg-neutral-600 rounded-l-full" style={{ width: "20%" }} />
                        <div className="h-full bg-emerald-500 rounded-r-full" style={{ width: "80%", boxShadow: "0 0 8px rgba(16,185,129,0.3)" }} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[9px] text-neutral-500">{"\u00A3"}89 without</span>
                      <span className="text-[9px] font-semibold text-emerald-400">{"\u00A3"}434.99 with Yestimate</span>
                    </div>
                  </div>

                  {/* ── Customer Approved — with glow ── */}
                  <div className="relative">
                    <div className="absolute inset-0 rounded-lg" style={{ boxShadow: "0 0 24px rgba(16,185,129,0.12), 0 0 48px rgba(16,185,129,0.06)" }} />
                    <div className="relative bg-emerald-500/[0.06] border border-emerald-500/25 rounded-lg px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        <span className="text-sm font-bold text-emerald-400">Customer Approved</span>
                      </div>
                      <span className="text-lg font-black text-white" style={{ fontFamily: fonts.mono }}>{"\u00A3"}434.99</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════ */}
      {/* SOCIAL PROOF MARQUEE                    */}
      {/* ═══════════════════════════════════════ */}
      <div className="py-4 border-y border-white/5 bg-[#0A0A0A] overflow-hidden" data-testid="social-proof-marquee">
        <div className="animate-marquee whitespace-nowrap flex items-center gap-12">
          {[
            "Southern Co. recovered £12,400 in missed work this month",
            "94% average customer approval rate",
            "Trusted by UK independent garages",
            "Zero missed inspection items",
            "£847 average additional revenue per vehicle",
            "Front of house workflow time reduced by 60%",
            "Southern Co. recovered £12,400 in missed work this month",
            "94% average customer approval rate",
            "Trusted by UK independent garages",
            "Zero missed inspection items",
            "£847 average additional revenue per vehicle",
            "Front of house workflow time reduced by 60%",
          ].map((text, i) => (
            <span key={i} className="inline-flex items-center gap-3 text-sm text-neutral-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/100/50" />
              {text}
            </span>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════ */}
      {/* SECTION 2 — FINANCIAL CALCULATOR        */}
      {/* ═══════════════════════════════════════ */}
      <section ref={calcRef} className="py-16 md:py-24 px-6 lg:px-12 bg-[#0A0A0A] border-y border-white/5" data-testid="calculator-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <SectionLabel><span className="text-emerald-500">Revenue calculator</span></SectionLabel>
            <h2 style={{ fontFamily: fonts.heading }}
              className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-3">
              See how much you're losing
            </h2>
            <p className="text-base text-neutral-400 max-w-lg mx-auto">
              Adjust the slider to match your workshop. The numbers are based on industry averages from real UK garages.
            </p>
          </div>

          {/* Slider */}
          <div className="max-w-md mx-auto mb-10">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-neutral-400">Cars per week</span>
              <span style={{ fontFamily: fonts.mono }}
                className="text-2xl font-bold text-white">{carsPerWeek}</span>
            </div>
            <Slider
              data-testid="calculator-slider"
              value={[carsPerWeek]}
              onValueChange={(v) => setCarsPerWeek(v[0])}
              min={5}
              max={60}
              step={1}
              className="mb-2"
            />
            <div className="flex justify-between text-[10px] text-neutral-400 font-medium">
              <span>5 cars</span>
              <span>60 cars</span>
            </div>
          </div>

          {/* Revenue cards */}
          <div className="grid md:grid-cols-3 gap-4" data-testid="calculator-results">
            {/* Per job */}
            <div className="bg-red-500/100/10 border border-red-500/20 rounded-xl p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-red-500/100" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-red-400">Lost per job</span>
              </div>
              <p style={{ fontFamily: fonts.heading }} className="text-3xl font-bold text-red-400" data-testid="calc-per-job">
                £{lostPerJob}
              </p>
              <p className="text-xs text-neutral-400 mt-1">Average missed revenue</p>
            </div>
            {/* Per week */}
            <div className="bg-amber-500/100/10 border border-amber-500/20 rounded-xl p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-amber-500/100" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-400">Lost per week</span>
              </div>
              <p style={{ fontFamily: fonts.heading }} className="text-3xl font-bold text-amber-400" data-testid="calc-per-week">
                £{weeklyLoss.toLocaleString()}
              </p>
              <p className="text-xs text-neutral-400 mt-1">Based on {carsPerWeek} cars/week</p>
            </div>
            {/* Per year — emerald glow */}
            <div className="bg-emerald-500/100/10 border border-emerald-500/20 rounded-xl p-6 text-center animate-pulse-glow">
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-emerald-500/100" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-400">Recoverable per year</span>
              </div>
              <p style={{ fontFamily: fonts.heading }} className="text-3xl font-bold text-emerald-400" data-testid="calc-per-year">
                £{yearlyLoss.toLocaleString()}
              </p>
              <p className="text-xs text-neutral-400 mt-1">With Yestimate's system</p>
            </div>
          </div>

          <div className="text-center mt-8">
            <button onClick={startDiagnosis} data-testid="calc-cta"
              className="group inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500/100 text-white text-sm font-semibold rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-emerald-500/20">
              Find your specific losses
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════ */}
      {/* FULL-SCREEN DEMO OVERLAY                */}
      {/* ═══════════════════════════════════════ */}
      {showFullDemo && (
        <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" data-testid="demo-overlay">
          <div className="w-full max-w-6xl">
            <CinematicDemo variant="full" autoPlay={true} defaultMuted={false} onClose={() => setShowFullDemo(false)} />
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════ */}
      {/* SECTION 3 — INTERACTIVE DIAGNOSIS       */}
      {/* ═══════════════════════════════════════ */}
      {diagnosisStarted && (
        <section ref={diagnosisRef} className="py-16 md:py-24 px-6 lg:px-12 bg-[#050505]" data-testid="diagnosis-section">
          <div className="max-w-7xl mx-auto">
            {!diagnosisComplete ? (
              <>
                <div className="flex items-center justify-between mb-10">
                  <div>
                    <SectionLabel><span className="text-emerald-500">Your diagnosis</span></SectionLabel>
                    <h2 style={{ fontFamily: fonts.heading }}
                      className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                      Where are you losing revenue?
                    </h2>
                  </div>
                  <ProgressBar current={currentQuestion} total={QUESTIONS.length} />
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="animate-fadeIn" key={currentQuestion}>
                    <div className="border border-white/10 rounded-xl p-8 bg-[#0F0F0F] min-h-[400px] flex flex-col justify-center">
                      <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-4">
                        Question {currentQuestion + 1} of {QUESTIONS.length}
                      </span>
                      <h3 style={{ fontFamily: fonts.heading }}
                        className="text-xl md:text-2xl font-bold tracking-tight text-white mb-6 leading-tight">
                        {QUESTIONS[currentQuestion].question}
                      </h3>
                      {showSimulation ? (
                        <div className="animate-fadeIn">
                          <p className="text-sm text-neutral-400 mb-4 leading-relaxed">
                            {QUESTIONS[currentQuestion].yesText}
                          </p>
                          <p className="text-xs text-neutral-400 mb-6">Watch the simulation to see how Yestimate handles this</p>
                        </div>
                      ) : (
                        <div className="flex gap-4">
                          <button onClick={() => handleAnswer("yes")} data-testid={`diagnosis-yes-${currentQuestion}`}
                            className="flex-1 py-4 px-6 border-2 border-white/10 hover:border-red-500/40 hover:bg-red-500/100/10 rounded-lg text-sm font-semibold text-neutral-300 hover:text-red-300 transition-all">
                            Yes, this is a problem
                          </button>
                          <button onClick={() => handleAnswer("no")} data-testid={`diagnosis-no-${currentQuestion}`}
                            className="flex-1 py-4 px-6 border-2 border-white/10 hover:border-emerald-500/40 hover:bg-emerald-500/100/10 rounded-lg text-sm font-semibold text-neutral-300 hover:text-emerald-300 transition-all">
                            No, we're good here
                          </button>
                        </div>
                      )}
                    </div>
                    {totalRevenue > 0 && (
                      <div className="mt-4 border border-red-500/20 rounded-xl p-4 bg-red-500/100/10 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <StatusBadge status="red" showLabel={false} />
                          <span className="text-xs font-semibold uppercase tracking-wider text-red-400">Revenue at risk per job</span>
                        </div>
                        <span style={{ fontFamily: fonts.heading }} className="text-lg font-bold text-red-400">
                          <AnimatedNumber value={totalRevenue} />
                        </span>
                      </div>
                    )}
                  </div>

                  <div>
                    {showSimulation === "brake" && <BrakeSimulation onComplete={goToNext} />}
                    {showSimulation === "steering" && <SteeringSimulation onComplete={goToNext} />}
                    {showSimulation === "diagnostic" && <DiagnosticSimulation onComplete={goToNext} />}
                    {!showSimulation && (
                      <div className="border border-white/10 rounded-xl p-8 bg-[#0F0F0F] min-h-[400px] flex flex-col items-center justify-center">
                        <img src={QUESTIONS[currentQuestion].img} alt="" className="w-48 h-48 object-contain opacity-10 mb-4" />
                        <p className="text-sm text-neutral-400 text-center">Answer to see how<br />Yestimate solves this</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════ */}
      {/* SECTION 4 — REVENUE IMPACT              */}
      {/* ═══════════════════════════════════════ */}
      {diagnosisComplete && (
        <section ref={resultsRef} className="py-16 md:py-24 px-6 lg:px-12 bg-neutral-900" data-testid="revenue-section">
          <div className="max-w-5xl mx-auto text-center animate-slideUp">
            <SectionLabel><span className="text-neutral-500">Your diagnosis result</span></SectionLabel>
            <h2 style={{ fontFamily: fonts.heading }}
              className="text-3xl md:text-5xl font-bold tracking-tight text-white mb-12 leading-tight">
              You don't need more cars.<br />
              <span className="text-emerald-400">You need to convert more of the work<br />you're already seeing.</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="rounded-xl p-8 border-l-4 border border-neutral-800" style={{ borderLeftColor: colors.red, backgroundColor: "rgba(239,68,68,0.06)" }}>
                <div className="flex items-center gap-2 justify-center mb-3">
                  <span className="w-2 h-2 rounded-full bg-red-500/100 animate-pulse" />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-red-400">Lost per job</p>
                </div>
                <p style={{ fontFamily: fonts.heading }} className="text-4xl font-bold text-red-400">
                  <AnimatedNumber value={totalRevenue || 100} />
                </p>
              </div>
              <div className="rounded-xl p-8 border-l-4 border border-neutral-800" style={{ borderLeftColor: colors.amber, backgroundColor: "rgba(245,158,11,0.06)" }}>
                <div className="flex items-center gap-2 justify-center mb-3">
                  <span className="w-2 h-2 rounded-full bg-amber-500/100 animate-pulse" />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-400">Lost per week</p>
                </div>
                <p style={{ fontFamily: fonts.heading }} className="text-4xl font-bold text-amber-400">
                  <AnimatedNumber value={weeklyDiag || 2000} duration={1500} />
                </p>
              </div>
              <div className="rounded-xl p-8 border-l-4 border border-neutral-800" style={{ borderLeftColor: colors.green, backgroundColor: "rgba(16,185,129,0.06)" }}>
                <div className="flex items-center gap-2 justify-center mb-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-500/100 animate-pulse" />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Recoverable per year</p>
                </div>
                <p style={{ fontFamily: fonts.heading }} className="text-4xl font-bold text-emerald-400">
                  <AnimatedNumber value={yearlyDiag || 100000} duration={2000} />
                </p>
              </div>
            </div>
            <button onClick={() => navigate('/app/login')} data-testid="revenue-cta"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white hover:bg-neutral-100 text-neutral-900 text-sm font-semibold rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-xl">
              Start Trial — Prove It On Your Jobs
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════ */}
      {/* SECTION 5 — BEFORE VS AFTER             */}
      {/* ═══════════════════════════════════════ */}
      <section className="py-16 md:py-24 px-6 lg:px-12" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <SectionLabel><span className="text-emerald-500">The difference</span></SectionLabel>
            <h2 style={{ fontFamily: fonts.heading }}
              className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Before vs After Yestimate
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-0 border border-white/10 rounded-xl overflow-hidden">
            <div className="p-8 md:p-10 bg-white/[0.02] border-b md:border-b-0 md:border-r border-white/10">
              <div className="flex items-center gap-2 mb-6">
                <X className="w-5 h-5 text-neutral-400" />
                <span className="text-sm font-semibold uppercase tracking-wider text-neutral-400">Without Yestimate</span>
              </div>
              <div className="space-y-3">
                {[
                  { icon: Eye, text: "Inconsistent inspections — items missed daily" },
                  { icon: FileCheck, text: "Incomplete estimates — underpriced or missing work" },
                  { icon: AlertTriangle, text: "Diagnostic guesswork — customer confusion" },
                  { icon: MessageSquare, text: "Poor communication — customers decline needed work" },
                  { icon: TrendingUp, text: "Revenue leaks — no visibility on what's lost" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-white/[0.02] border border-white/5 rounded-lg">
                    <item.icon className="w-4 h-4 text-neutral-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-neutral-400">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-8 md:p-10 bg-[#0F0F0F]">
              <div className="flex items-center gap-2 mb-6">
                <Check className="w-5 h-5 text-emerald-500" />
                <span className="text-sm font-semibold uppercase tracking-wider text-emerald-500">With Yestimate</span>
              </div>
              <div className="space-y-3">
                <StatusBorderCard status="red" className="px-3 py-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5"><StatusBadge status="red" /></div>
                    <div>
                      <p className="text-sm font-semibold text-white/80">Safety-critical items flagged immediately</p>
                      <p className="text-xs text-neutral-400 mt-0.5">Measurement-based evidence. Mandatory photos.</p>
                    </div>
                  </div>
                </StatusBorderCard>
                <StatusBorderCard status="amber" className="px-3 py-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5"><StatusBadge status="amber" /></div>
                    <div>
                      <p className="text-sm font-semibold text-white/80">Deteriorating items tracked for planning</p>
                      <p className="text-xs text-neutral-400 mt-0.5">Paired items detected. Costs prevented.</p>
                    </div>
                  </div>
                </StatusBorderCard>
                <StatusBorderCard status="green" className="px-3 py-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5"><StatusBadge status="green" /></div>
                    <div>
                      <p className="text-sm font-semibold text-white/80">Healthy systems confirmed — customer reassured</p>
                      <p className="text-xs text-neutral-400 mt-0.5">Nothing missed. Full transparency.</p>
                    </div>
                  </div>
                </StatusBorderCard>
                <div className="flex items-start gap-3 p-3 bg-blue-500/100/10 border border-blue-500/20 rounded-lg">
                  <Zap className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-white/80">AI-powered customer explanations</p>
                    <p className="text-xs text-neutral-500 mt-0.5">Customers approve with confidence.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════ */}
      {/* SECTION 6 — FULL JOB JOURNEY            */}
      {/* ═══════════════════════════════════════ */}
      <section className="py-16 md:py-24 px-6 lg:px-12 bg-[#0A0A0A] border-y border-white/5" data-testid="journey-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <SectionLabel><span className="text-emerald-500">The complete workflow</span></SectionLabel>
            <h2 style={{ fontFamily: fonts.heading }}
              className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
              One connected system. Every step covered.
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-0 border border-white/10 rounded-xl overflow-hidden">
            {[
              {
                step: "01", title: "Technician Inspects",
                desc: "Structured inspection. Mandatory evidence. Measurement-based classification.",
                icon: Eye,
                statusItems: [
                  { status: "red", text: "2.5mm pads — replace" },
                  { status: "amber", text: "Play in track rod" },
                  { status: "green", text: "Rear brakes OK" },
                ],
              },
              {
                step: "02", title: "FOH Builds & Prices",
                desc: "Intelligent job builder. Pair logic. Shared labour. Complete estimates.",
                icon: Wrench,
                statusItems: [
                  { status: "red", text: "Brake job — £434.99" },
                  { status: "amber", text: "Steering — £328.00" },
                ],
              },
              {
                step: "03", title: "Customer Decides",
                desc: "Clear explanation engine. Evidence-based. Customers approve with confidence.",
                icon: Users,
                statusItems: [
                  { status: "red", text: "Approved — urgent" },
                  { status: "amber", text: "Plan ahead" },
                  { status: "green", text: "All clear" },
                ],
              },
              {
                step: "04", title: "Work Recovery",
                desc: "Declined items tracked. Follow-up engine. Every finding has a conversion path.",
                icon: TrendingUp,
                statusItems: [
                  { status: "amber", text: "Declined — follow up" },
                  { status: "green", text: "Recovered — £328" },
                ],
              },
            ].map((s, i) => (
              <div key={i} className={`p-7 ${i < 3 ? 'border-b md:border-b-0 md:border-r border-white/5' : ''}`}>
                <span style={{ fontFamily: fonts.mono }}
                  className="text-xs font-bold text-neutral-400 mb-4 block">{s.step}</span>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 bg-white/[0.03] border border-white/10">
                  <s.icon className="w-5 h-5 text-neutral-400" />
                </div>
                <h3 className="text-base font-bold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed mb-4">{s.desc}</p>
                <div className="space-y-1.5">
                  {s.statusItems.map((si, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: STATUS_CONFIG[si.status].dot }} />
                      <span className="text-[11px] text-neutral-400">{si.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════ */}
      {/* SECTION 7 — CUSTOMER DECISION ENGINE     */}
      {/* ═══════════════════════════════════════ */}
      <section className="py-16 md:py-24 px-6 lg:px-12" data-testid="decision-engine-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <SectionLabel><span className="text-emerald-500">Customer Decision Engine</span></SectionLabel>
            <h2 style={{ fontFamily: fonts.heading }}
              className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-3">
              Customers see what matters. In the right order.
            </h2>
            <p className="text-base text-neutral-400 max-w-2xl mx-auto">
              The colour system makes it immediately obvious: what is urgent, what needs planning, and what is fine.
            </p>
          </div>

          <div className="max-w-sm mx-auto">
            <div className="border border-white/10 rounded-xl overflow-hidden bg-[#0F0F0F]" data-testid="decision-engine-mock">
              <div className="bg-neutral-900 px-5 py-3 flex items-center justify-between border-b border-white/5">
                <span className="text-white text-xs font-semibold uppercase tracking-wider">Customer Portal</span>
                <span className="text-neutral-500 text-[10px]">AB12 CDE</span>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <StatusBadge status="red" size="sm" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-red-400">Requires immediate action</span>
                  </div>
                  <StatusBorderCard status="red" className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-white/80">Front Brake Pads & Discs</span>
                      <span className="text-sm font-bold text-white/80">£434.99</span>
                    </div>
                    <p className="text-xs text-neutral-400">Measured at 2.5mm — below safe minimum.</p>
                    <div className="flex gap-2">
                      <button data-testid="mock-approve-red" className="flex-1 py-1.5 bg-emerald-600 text-white text-[10px] font-bold rounded-md text-center">Approve</button>
                      <button data-testid="mock-decline-red" className="flex-1 py-1.5 bg-white/5 border border-white/10 text-neutral-400 text-[10px] font-bold rounded-md text-center">Decline</button>
                    </div>
                  </StatusBorderCard>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <StatusBadge status="amber" size="sm" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400">Plan ahead</span>
                  </div>
                  <StatusBorderCard status="amber" className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-white/80">Track Rod Ends & Alignment</span>
                      <span className="text-sm font-bold text-white/80">£328.00</span>
                    </div>
                    <p className="text-xs text-neutral-400">Excessive play. Prevents tyre wear and higher costs.</p>
                    <div className="flex gap-2">
                      <button data-testid="mock-approve-amber" className="flex-1 py-1.5 bg-emerald-600 text-white text-[10px] font-bold rounded-md text-center">Approve</button>
                      <button data-testid="mock-decline-amber" className="flex-1 py-1.5 bg-white/5 border border-white/10 text-neutral-400 text-[10px] font-bold rounded-md text-center">Decline</button>
                    </div>
                  </StatusBorderCard>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <StatusBadge status="green" size="sm" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">No action required</span>
                  </div>
                  <StatusBorderCard status="green" className="p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-400">Rear Brakes, Battery, Exhaust</span>
                      <span className="text-xs text-emerald-400 font-semibold">All OK</span>
                    </div>
                  </StatusBorderCard>
                </div>
                <div className="pt-2 border-t border-white/5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-neutral-400">Total</span>
                    <span style={{ fontFamily: fonts.heading }} className="text-lg font-bold text-white">£762.99</span>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-neutral-400 mt-4">Mobile-first. Structured by urgency.</p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════ */}
      {/* SECTION 8 — TRUST                       */}
      {/* ═══════════════════════════════════════ */}
      <section className="py-16 md:py-24 px-6 lg:px-12 bg-[#0A0A0A] border-y border-white/5" data-testid="trust-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <SectionLabel><span className="text-emerald-500">Built different</span></SectionLabel>
              <h2 style={{ fontFamily: fonts.heading }}
                className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6">
                Built inside a real<br />working garage.
              </h2>
              <p className="text-base text-neutral-400 leading-relaxed mb-6">
                Yestimate wasn't designed in a boardroom. It was built on the workshop floor — solving real problems that real garages face every day.
              </p>
              <p className="text-base text-neutral-400 leading-relaxed mb-8">
                Every feature exists because a technician needed it, a front of house person requested it, or a customer interaction proved it was necessary.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { val: "£150+", label: "Avg recovered per job" },
                  { val: "40%", label: "Higher authorisation rate" },
                  { val: "90%+", label: "Inspection consistency" },
                  { val: "Real", label: "Garage tested & proven" },
                ].map((s, i) => (
                  <div key={i} className="border border-white/10 rounded-xl p-4 bg-white/[0.02]">
                    <p style={{ fontFamily: fonts.heading }} className="text-xl font-bold text-white">{s.val}</p>
                    <p className="text-xs text-neutral-400 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden md:block">
              <img src={media.garageOwner} alt="Garage professional"
                className="w-full rounded-xl border border-white/10 opacity-80" />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════ */}
      {/* SECTION 9 — FINAL CTA (URGENCY)         */}
      {/* ═══════════════════════════════════════ */}
      <section className="py-20 md:py-32 px-6 lg:px-12 bg-[#050505] relative overflow-hidden" data-testid="final-cta-section">
        {/* Subtle emerald glow background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[600px] rounded-full bg-emerald-500/100/5 blur-[120px]" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          {/* Urgency badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/100/10 border border-emerald-500/20 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/100 animate-pulse" />
            <span className="text-xs font-semibold text-emerald-400">Limited early access — free during beta</span>
          </div>
          <h2 style={{ fontFamily: fonts.heading }}
            className="text-3xl md:text-5xl font-bold tracking-tighter text-white mb-6 leading-tight">
            Every week without Yestimate<br />is revenue you'll never recover.
          </h2>
          <p className="text-base md:text-lg text-neutral-300 mb-10 max-w-lg mx-auto">
            Start small. Measure the difference. See how much revenue you're actually leaving behind.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/app/login')} data-testid="final-start-trial"
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-emerald-600 hover:bg-emerald-500/100 text-white text-sm font-bold rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-500/20">
              Start Free Trial
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate('/demo')} data-testid="final-see-demo"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white/5 border border-white/10 hover:border-white/20 text-neutral-300 hover:text-white text-sm font-semibold rounded-lg transition-all hover:-translate-y-0.5">
              <Play className="w-5 h-5" />
              See It In Action
            </button>
            <button data-testid="final-book-demo"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-transparent border border-white/10 hover:border-white/20 text-neutral-400 hover:text-white text-sm font-semibold rounded-lg transition-all">
              <Phone className="w-5 h-5" />
              Book a Demo
            </button>
          </div>
          <p className="text-xs text-neutral-400 mt-6">No credit card required. Your team is up and running in minutes.</p>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-8 px-6 lg:px-12 border-t border-white/5 bg-[#050505]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/yestimate-logo.png" alt="Yestimate" className="h-14 w-auto"
            style={{ filter: "brightness(1.3) contrast(1.15) drop-shadow(0 0 16px rgba(16,185,129,0.2))" }} />
          <p className="text-xs text-neutral-300">A better way to run your workshop.</p>
        </div>
      </footer>

      {/* ─── STICKY FLOATING CTA (appears on scroll) ─── */}
      <div className="fixed bottom-6 right-6 z-50 hidden md:block" data-testid="floating-cta">
        <button onClick={() => navigate('/app/login')}
          className="group inline-flex items-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-500/100 text-white text-sm font-bold rounded-full shadow-lg shadow-emerald-500/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-500/30">
          Start Free Trial
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* ─── Mobile desktop hint ─── */}
      <div className="md:hidden fixed bottom-4 left-4 right-4 bg-neutral-900/90 backdrop-blur-sm text-white text-xs text-center py-2 rounded-lg z-40 border border-white/10">
        For the full interactive experience, view on desktop
      </div>
    </div>
  );
};

export default MarketingHomepage;
